import i18n from 'i18next'
import moment from 'moment'
import numeral from 'numeral'
import _ from 'lodash'

export const DATE_COLUMN = 'Fecha'

export const AMOUNT_COLUMNS = ['amount', 'amount_origin']
const DETAIL_COLUMNS_LABEL = {
  [DATE_COLUMN]: 'FIELD_DATE',
  amount: 'FIELD_AMOUNT',
  transid: 'CONTROL_DETAIL_COLUMN_TRANSID',
  comment: 'FIELD_COMMENT',
  transtype: 'CONTROL_DETAIL_COLUMN_TRANSTYPE',
  baseref: 'CONTROL_DETAIL_COLUMN_BASEREF',
  currency_origin: 'CONTROL_DETAIL_COLUMN_CURRENCY_ORIGIN',
  amount_origin: 'CONTROL_DETAIL_COLUMN_AMOUNT_ORIGIN',
}
const HIDDEN_COLUMNS = ['id', 'price', 'quantity']

const asValueList = (value) => {
  if (_.isArray(value)) return value
  if (_.isPlainObject(value)) return _.keys(value)
  return value != null ? [value] : []
}

export const splitCellFilters = (cellFilters = {}) => {
  const months = []
  const dimensionFilters = {}

  Object.entries(cellFilters).forEach(([key, value]) => {
    const values = asValueList(value)
    if (key === DATE_COLUMN) {
      months.push(...values.filter((month) => moment(month, 'YYYY-MM', true).isValid()))
    } else if (!_.isEmpty(values)) {
      dimensionFilters[key] = values
    }
  })

  return { months, dimensionFilters }
}

const dimensionValuesCache = {}

export const fetchCachedDimensionValues = (dimensionId, fetchDimensionValues) => {
  if (!dimensionValuesCache[dimensionId]) {
    dimensionValuesCache[dimensionId] = fetchDimensionValues(dimensionId).catch((error) => {
      delete dimensionValuesCache[dimensionId]
      throw error
    })
  }
  return dimensionValuesCache[dimensionId]
}

export const findDimension = (columnName, allDimensionsList = []) =>
  allDimensionsList.find(
    (dimension) =>
      dimension.name_destination === columnName ||
      dimension.name_origin === columnName ||
      dimension.name === columnName,
  )

const displayedValues = ({ code_destination, name_destination }) =>
  _.uniq([code_destination, name_destination, `${code_destination} | ${name_destination}`])

const buildValuesIndex = (dimensionValues = []) => {
  const index = new Map()

  dimensionValues.forEach((dimensionValue) => {
    displayedValues(dimensionValue).forEach((label) => {
      const key = `${label}`
      if (index.has(key)) index.get(key).push(dimensionValue.id)
      else index.set(key, [dimensionValue.id])
    })
  })

  return index
}

const valuesIndexCache = new WeakMap()

const getValuesIndex = (dimensionValues = []) => {
  if (!valuesIndexCache.has(dimensionValues)) {
    valuesIndexCache.set(dimensionValues, buildValuesIndex(dimensionValues))
  }
  return valuesIndexCache.get(dimensionValues)
}

const resolveValueIds = (valuesIndex, value) => valuesIndex.get(`${value}`) || []

export const buildDimensionFilter = async ({
  dimensionFilters,
  allDimensionsList,
  fetchDimensionValues,
}) => {
  const dimensionFilter = {}
  const unresolvedColumns = []

  await Promise.all(
    Object.entries(dimensionFilters).map(async ([columnName, values]) => {
      const dimension = findDimension(columnName, allDimensionsList)
      if (!dimension) {
        unresolvedColumns.push(columnName)
        return
      }

      const dimensionValues = await fetchDimensionValues(dimension.id)
      const valuesIndex = getValuesIndex(dimensionValues)
      const resolvedIds = values.map((value) => resolveValueIds(valuesIndex, value))

      if (resolvedIds.some(_.isEmpty)) {
        unresolvedColumns.push(columnName)
        return
      }

      dimensionFilter[dimension.id] = _.uniq(_.flatten(resolvedIds))
    }),
  )

  return { dimensionFilter, unresolvedColumns }
}

const renderAmount = (value) => (value != null ? numeral(value).format('0,0.00') : '-')

export const generateDetailColumns = (rows = []) => {
  if (_.isEmpty(rows)) return []

  return Object.keys(rows[0])
    .filter((key) => !HIDDEN_COLUMNS.includes(key))
    .map((key) => {
      const label = DETAIL_COLUMNS_LABEL[key]
      const column = {
        title: label ? i18n.t(label) : key,
        dataIndex: key,
        key,
        ellipsis: true,
      }

      if (key === DATE_COLUMN) {
        return {
          ...column,
          width: 110,
          fixed: 'left',
          render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
        }
      }

      if (AMOUNT_COLUMNS.includes(key)) {
        return { ...column, width: 130, align: 'right', render: renderAmount }
      }

      return { ...column, width: 150, render: (value) => (value != null ? value : '-') }
    })
}
